import { addEventListener, removeListenersFrom } from "@/helpers/clickManager";
import { lookAtCamera } from "@/helpers/ipr";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

interface NewLineCallbackArgs {
  action: "add" | "remove";
  point1?: THREE.Vector3;
  point2?: THREE.Vector3;
  object1?: THREE.Object3D;
  object2?: THREE.Object3D;
  distance?: number;
  index?: number;
}

class MeasurementTool {
  scene: THREE.Scene | null;
  camera: THREE.Camera | null;
  controls: OrbitControls | null;
  canvas: HTMLCanvasElement | null;
  points: THREE.Vector3[];
  lines: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>[];
  linesGroup: THREE.Group;
  activeLine: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> | null;
  sphereRadius: number;
  enable: boolean;
  isCameraMoving: boolean;
  objectsToCheck: THREE.Object3D[];
  lineCloseBTNs: THREE.Object3D[];
  activePointObject: THREE.Mesh | null;
  intersectedObjects: THREE.Object3D[];
  spheres: THREE.Mesh[];
  labels: THREE.Mesh[];
  newLineAddedCallback: ((args: NewLineCallbackArgs) => void) | null;
  raycaster: THREE.Raycaster;
  activePoint: THREE.Vector3 | null;
  activeLineLabel: THREE.Mesh | null;
  movementEventCountChiller: number; // this is to make sure the movement event not called with extreme count
  chillCounter: number;
  sphereGeometry: THREE.SphereGeometry | null;
  sphereMaterial: THREE.MeshStandardMaterial | null;
  lineMaterial: THREE.LineBasicMaterial | null;
  closeBTNMaterial: THREE.MeshBasicMaterial | null;
  isMouseDown: boolean;
  isMouseMove: boolean;
  currentObject: THREE.Object3D | THREE.Mesh | null;
  previousMaterial: THREE.Material | null;
  isSplit: boolean;

  constructor() {
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.canvas = null;
    this.points = [];
    this.lines = [];
    this.linesGroup = new THREE.Group();
    this.activeLine = null;
    this.sphereRadius = 0.5;
    this.enable = false;
    this.isCameraMoving = false;
    this.objectsToCheck = [];
    this.lineCloseBTNs = [];
    this.activePointObject = null;
    this.intersectedObjects = [];
    this.spheres = [];
    this.labels = [];
    this.newLineAddedCallback = null;
    this.raycaster = new THREE.Raycaster();
    this.activePoint = null;
    this.activeLineLabel = null;
    this.movementEventCountChiller = 3;
    this.chillCounter = 0;
    this.sphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 16, 16);
    this.sphereMaterial = new THREE.MeshStandardMaterial({
      color: "green",
      transparent: true,
      opacity: 0.5,
    });
    this.lineMaterial = new THREE.LineBasicMaterial({
      color: "black",
      linewidth: 2,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1.0, // Adjust as needed
    });
    this.closeBTNMaterial = new THREE.MeshBasicMaterial({
      map: new THREE.TextureLoader().load("Ruler/Ruler Close BTN.png"),
      transparent: true,
      side: THREE.DoubleSide,
      toneMapped: false,
      depthTest: false,
      depthWrite: false,
    });
    this.isMouseDown = false;
    this.isMouseMove = false;
    this.currentObject = null;
    this.isSplit = false;
    // Bind event handlers
    this.handleClick = this.handleClick.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleCameraChange = this.handleCameraChange.bind(this);
    this.handleCameraStop = this.handleCameraStop.bind(this);
  }

  attach({
    scene,
    camera,
    controls,
    canvas,
  }: {
    scene: THREE.Scene;
    camera: THREE.Camera;
    controls: OrbitControls;
    canvas: HTMLCanvasElement;
  }) {
    this.scene = scene;
    this.camera = camera;
    this.controls = controls;
    this.canvas = canvas;
    this.linesGroup.name = "lines Group";
    scene.add(this.linesGroup);
    this.initControls();
  }
  //adding new objects with the old ones
  addObjectsToCheck(objects: THREE.Object3D[]) {
    this.objectsToCheck.push(...objects);
  }
  //removing prev objects
  addNewObjectToCheck(object: THREE.Object3D) {
    if (!object) {
      return;
    }

    this.objectsToCheck.push(object);
  }
  addNewObjectsToCheck(objects: THREE.Object3D[]) {
    this.objectsToCheck = [...objects];
  }

  handleClick(event: MouseEvent) {
    if (
      !this.enable ||
      this.isCameraMoving ||
      !this.camera ||
      !this.canvas ||
      this.isMouseMove
    ) {
      this.isMouseDown = false;
      this.isMouseMove = false;
      return;
    } else {
      const rect = this.canvas.getBoundingClientRect();
      const x =
        ((event.clientX * (this.isSplit ? 2 : 1) - rect.left) /
          (window.innerWidth - rect.left)) *
          2 -
        1;
      const y =
        (-(event.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;
      this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);
      const visibleObjects = [];
      this.objectsToCheck.forEach(obj => {
        if (this.isVisible(obj)) {
          visibleObjects.push(obj);
        }
      });

      this.lineCloseBTNs.forEach(obj => {
        if (this.isVisible(obj)) {
          visibleObjects.push(obj);
        }
      });

      const intersects = this.raycaster.intersectObjects(visibleObjects, false);

      if (intersects.length > 0) {
        const closeBTNIndex = this.checkForCloseBTNClick(intersects);
        if (typeof closeBTNIndex === "number") {
          if (
            intersects[closeBTNIndex].object.parent.parent instanceof THREE.Line
          ) {
            const lineIndex = this.lines.indexOf(
              intersects[closeBTNIndex].object.parent.parent,
            );
            this.lineCloseBTNs.splice(
              this.lineCloseBTNs.indexOf(intersects[closeBTNIndex].object),
              1,
            );
            this.removeLine(lineIndex);
          }
        } else {
          const intersect = intersects[0];
          this.addPoint(intersect.point.clone(), intersect.object);
        }
      }
    }
    this.isMouseDown = false;
    this.isMouseMove = false;
  }
  checkForCloseBTNClick(intersects: THREE.Intersection[]): number {
    for (
      let intersectIndex = 0;
      intersectIndex < intersects.length;
      intersectIndex++
    ) {
      if (intersects[intersectIndex].object.name.includes("close")) {
        return intersectIndex;
      }
    }
  }
  addPoint(point: THREE.Vector3, intersectedObject: THREE.Object3D) {
    this.points.push(point);
    this.intersectedObjects.push(intersectedObject);
    this.renderSphereAtPoint(point);

    if (this.points.length % 2 === 0) {
      const [point1, point2] = [
        this.points[this.points.length - 2],
        this.points[this.points.length - 1],
      ];
      const [obj1, obj2] = [
        this.intersectedObjects[this.intersectedObjects.length - 2],
        this.intersectedObjects[this.intersectedObjects.length - 1],
      ];
      this.addLine(point1, point2, obj1, obj2);
      this.removeActiveLine();
    }
  }

  renderLineLength(
    point1: THREE.Vector3,
    point2: THREE.Vector3,
    active = false,
  ): THREE.Mesh {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;
    canvas.width = 150;
    canvas.height = 50;
    context.fillStyle = "black";
    context.font = "bold 16px sans-serif";
    context.clearRect(0, 0, canvas.width, canvas.height);
    const distance = point1.distanceTo(point2).toFixed(2);
    context.fillText(`${distance} mm`, 10, 20);
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      transparent: true,
      depthTest: false, // Disable depth test so it's always rendered on top
    });

    const planeGeometry = new THREE.PlaneGeometry(10, 5);
    const plane = new THREE.Mesh(planeGeometry, material);

    // Set the render order to ensure it renders above other objects
    plane.renderOrder = 9999;

    if (!active) {
      this.labels.push(plane);
    }

    plane.position.copy(
      new THREE.Vector3().addVectors(point1, point2).multiplyScalar(0.5),
    );
    lookAtCamera(plane);
    // plane.lookAt(this.camera!.position);

    const updateLabelOrientation = () => {
      lookAtCamera(plane);

      // plane.lookAt(this.camera!.position);
      requestAnimationFrame(updateLabelOrientation);
    };
    requestAnimationFrame(updateLabelOrientation);

    return plane;
  }

  renderCloseBTN() {
    const geometry = new THREE.CircleGeometry(0.5, 16);
    const circle = new THREE.Mesh(geometry, this.closeBTNMaterial.clone());
    circle.name = "close sign BTN";
    return circle;
  }

  addLine(
    point1: THREE.Vector3,
    point2: THREE.Vector3,
    obj1: THREE.Object3D,
    obj2: THREE.Object3D,
  ) {
    const geometry = new THREE.BufferGeometry().setFromPoints([point1, point2]);
    const line = new THREE.Line(geometry, this.lineMaterial);
    line.name = "line";
    this.linesGroup!.add(line);
    this.lines.push(line);
    const lengthLabel = this.renderLineLength(point1, point2);
    lengthLabel.name = "distance";
    const closeBTN = this.renderCloseBTN();
    this.lineCloseBTNs.push(closeBTN);
    lengthLabel!.add(closeBTN);
    line!.add(lengthLabel);
    closeBTN.position.set(1.15, 1, 0);

    if (this.newLineAddedCallback) {
      this.newLineAddedCallback({
        action: "add",
        point1,
        point2,
        object1: obj1,
        object2: obj2,
        distance: point1.distanceTo(point2) * 1000,
      });
    }
  }

  renderSphereAtPoint(position: THREE.Vector3) {
    const geometry = new THREE.SphereGeometry(this.sphereRadius, 16, 16);
    const sphere = new THREE.Mesh(geometry, this.sphereMaterial);
    sphere.name = "sphere";
    sphere.position.copy(position);
    this.spheres.push(sphere);
    this.linesGroup!.add(sphere);
  }

  handleMouseMove(event: MouseEvent) {
    if (this.isMouseDown) {
      this.isMouseMove = true;
    }
    if (this.chillCounter < this.movementEventCountChiller) {
      this.chillCounter++;
      return;
    }
    this.chillCounter = 0;

    if (!this.enable || this.isCameraMoving || !this.camera || !this.canvas)
      return;

    const rect = this.canvas.getBoundingClientRect();
    const x =
      ((event.clientX * (this.isSplit ? 2 : 1) - rect.left) /
        (window.innerWidth - rect.left)) *
        2 -
      1;
    const y = (-(event.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;

    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

    const visibleObjects = [];
    this.objectsToCheck.forEach(obj => {
      if (this.isVisible(obj)) {
        visibleObjects.push(obj);
      }
    });

    const intersects = this.raycaster.intersectObjects(visibleObjects, false);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      this.setCurrentObject(intersect.object);
      this.activePoint = intersect.point;
      this.renderActivePoint(this.activePoint);
      if (this.points.length % 2 === 1) {
        this.renderActiveLine(
          this.points[this.points.length - 1],
          this.activePoint,
        );
      }
    } else {
      this.setCurrentObject(null);
    }
    this.chillCounter = 0;
  }

  setCurrentObject(object) {
    if (this.currentObject && this.currentObject instanceof THREE.Mesh) {
      this.currentObject.material = this.previousMaterial;
    }
    if (object && object instanceof THREE.Mesh) {
      this.previousMaterial = object.material;
      object.material = object.material.clone();
      object.material.color.multiplyScalar(0.7);
    }
    this.currentObject = object;
  }

  renderActivePoint(position: THREE.Vector3) {
    if (this.activePointObject) {
      this.linesGroup!.remove(this.activePointObject);
    }
    this.activePointObject = this.sphere(position);
    this.linesGroup!.add(this.activePointObject);
  }

  renderActiveLine(start: THREE.Vector3, end: THREE.Vector3) {
    if (this.activeLine) {
      this.activeLine.geometry.setFromPoints([start, end]);
      if (this.activeLineLabel) {
        this.linesGroup!.remove(this.activeLineLabel);
      }
      this.activeLineLabel = this.renderLineLength(start, end, true);
      this.linesGroup!.add(this.activeLineLabel);
    } else {
      const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
      this.activeLine = new THREE.Line(geometry, this.lineMaterial);
      this.linesGroup!.add(this.activeLine);
      this.activeLineLabel = this.renderLineLength(start, end, true);
      this.linesGroup!.add(this.activeLineLabel);
    }
  }

  removeActiveLine() {
    if (this.activeLine) {
      this.linesGroup!.remove(this.activeLine);
      this.linesGroup!.remove(this.activeLineLabel!);
      this.activeLine.geometry.dispose();
      this.activeLine.material.dispose();
      this.activeLine = null;
    }
  }

  removeLine(index: number) {
    const line = this.lines[index];
    if (line) {
      this.linesGroup!.remove(line);
      line.geometry.dispose();
      line.material.dispose();
      const point1 = this.spheres[index * 2];
      const point2 = this.spheres[index * 2 + 1];
      if (point1) this.linesGroup!.remove(point1);
      if (point2) this.linesGroup!.remove(point2);
      this.linesGroup!.remove(this.labels[index]);
      this.lines.splice(index, 1);
      this.labels.splice(index, 1);
      this.spheres.splice(index * 2, 2);
      this.intersectedObjects.splice(index * 2, 2);

      if (this.newLineAddedCallback) {
        this.newLineAddedCallback({ action: "remove", index });
      }
    }
  }

  onNewLineAdded(callback: (args: NewLineCallbackArgs) => void) {
    this.newLineAddedCallback = callback;
  }

  toggleMeasurement() {
    this.enable = !this.enable;
    this.linesGroup.visible = this.enable;
    if (!this.enable) {
      this.clearActivePoint();
      this.removeActiveLine();
      //making sure if user canceld after first click to clear the last pointy

      // if (this.points.length % 2 !== 0) {
      //   this.linesGroup.remove(this.spheres[this.points.length - 1]);
      //   this.points.splice(-1);
      // }
    } else {
      // this.controls!.enabled = false;
    }
  }

  handleCameraChange() {
    this.isCameraMoving = true;
  }

  handleCameraStop() {
    this.isCameraMoving = false;
  }

  initControls() {
    this.canvas?.addEventListener("mouseup", this.handleClick);
    this.canvas?.addEventListener("mousedown", () => {
      this.isMouseDown = true;
    });
    this.canvas?.addEventListener("mousemove", this.handleMouseMove);
    this.controls?.addEventListener("start", this.handleCameraChange);
    this.controls?.addEventListener("end", this.handleCameraStop);
  }

  dispose() {
    if (this.canvas) {
      this.canvas.removeEventListener("click", this.handleClick);
      this.canvas.removeEventListener("mousemove", this.handleMouseMove);
    }
    if (this.controls) {
      this.controls.removeEventListener("start", this.handleCameraChange);
      this.controls.removeEventListener("end", this.handleCameraStop);
    }
    if (this.activePointObject) {
      this.linesGroup!.remove(this.activePointObject);
      this.activePointObject = null;
    }
  }

  clearActivePoint() {
    if (this.activePointObject) {
      this.linesGroup!.remove(this.activePointObject);
    }
  }

  isVisible(object: THREE.Object3D, traverses: number = 10): boolean {
    let isVisibleResult: boolean = object.visible;
    if (object instanceof THREE.Mesh) {
      isVisibleResult = isVisibleResult && object.material.opacity === 1;
    }
    let parentIndex: number = 1;
    let parent: THREE.Object3D = object.parent;

    while (isVisibleResult && parent && parentIndex < traverses) {
      isVisibleResult = parent.visible;
      parent = parent.parent;
      parentIndex++;
    }
    return isVisibleResult;
  }
  sphere(position: THREE.Vector3): THREE.Mesh {
    const sphere = new THREE.Mesh(this.sphereGeometry, this.sphereMaterial);
    sphere.position.copy(position);
    return sphere;
  }

  getAllLines() {
    return this.lines;
  }
}

export default MeasurementTool;
